import fetch from 'isomorphic-fetch';
import _cloneDeep from 'lodash/cloneDeep';
import _find from 'lodash/find';
import _isEqual from 'lodash/isEqual';
import _pick from 'lodash/pick';
import _sortBy from 'lodash/sortBy';
import _uniqBy from 'lodash/uniqBy';
import _without from 'lodash/without';

import { showErrorBar } from 'actions/errorBar';
import {
  updateShareVideo,
  updateShareVideoDownloadUrl,
  updateShareVideoInTrimMode,
} from 'actions/shareVideo';
import {
  addToPlayRangeUndoStack,
  enterTrimMode,
  leaveTrimMode,
  redoPlayRangeChange,
  resetPlayRangeUndoStack,
  setSendingNewTrim,
  undoPlayRangeChange,
  updateCurrentTrimRange,
  updatePlayRanges,
  updateSelectedTrimBound,
  updateSelectedTrimRange,
  updateTrimNewChanges,
  updateTrimOriginalRanges,
  updateTrimRanges,
  updateTrimRedoStack,
  updateTrimUndoStack,
} from 'actions/trimming';
import {
  requestVideoPlayerCurrentTime,
  requestVideoPlayerPause,
  updateTheaterMode,
  updateVideoPlayerCurrentTime,
  updateVideoPlayerDisplayedDuration,
  updateVideoPlayerTimeOffset,
} from 'actions/videoPlayer';
import { ERROR } from 'constants/error-severities';
import { TRIM_PUBLISHED } from 'constants/events';
import { HEADER_CONTENT_TYPE } from 'constants/http';
import { TRIM_ACTION_DISPATCHED } from 'constants/messages';
import { JSON as JSON_MIME } from 'constants/mimes';
import {
  ADD_TRIM_OPERATION,
  ENTER_TRIM_MODE_OPERATION,
  LEAVE_TRIM_MODE_OPERATION,
  MIN_PLAY_RANGE_DURATION,
  REDO_TRIM_OPERATION,
  REMOVE_TRIM_OPERATION,
  UNDO_TRIM_OPERATION,
  UPDATE_CURRENT_TRIM_RANGE_OPERATION,
  UPDATE_SELECTED_TRIM_BOUND_OPERATION,
  UPDATE_SELECTED_TRIM_RANGE_OPERATION,
  UPDATE_TRIM_OPERATION,
} from 'constants/trimming';
import {
  updateVideoCreator,
  updateVideoPlayerSourceCreator,
} from 'creators/videoPlayer';
import {
  selectPlayRanges,
  selectSelectedPlayRangeIdx,
} from 'selectors/trimming';
import { selectPlayerTime, selectVideoDuration } from 'selectors/videoPlayer';
import * as analytics from 'utilities/analytics';
import * as sentry from 'utilities/sentry';
import {
  convertFullTimeToTrimTime,
  getTrimEventProperties,
  getTrimVideoDuration,
  makePlayRangeId,
  makePlayRangesFromTrimRanges,
  makeTrimRangesFromPlayRanges,
} from 'utilities/trimming';

const UNKNOWN_TRIM_OPERATION = new Error('unknown trim operation');

const MINIMUM_TOTAL_VIDEO_TIME = 1; // 1 second

/**
  For the undo/redo actions, we have a `redo_stack` and `undo_stack`. The basic
  idea is:

  1. Each time we perform a *new* action, we should add the complement of that
     action to the `undo_stack` and clear everything in the `redo_stack`.

     * Add a trim range with Id 4444
     * Add an operation to remove the trim with Id of 4444

  2. Each time we perform a redo/undo, we should pop off the respective stack,
     apply the operation, find the compliment of that operation, and then add to
     the other stack. An example that builds off the previous example.

     * We call undo
     * Pop off the `undo_stack` and then run the operation which removes the trim
       with Id 4444
     * Find the complement, which is adding a Trim of Id 4444
     * Add "add trim Id 4444" to the redo stack
**/

function callCreatorForTrimOperation(trimOperation) {
  const { trimRange, type } = trimOperation;

  switch (type) {
    case ADD_TRIM_OPERATION:
      return addTrimRangeCreator(trimRange, true);
    case REMOVE_TRIM_OPERATION:
      return removeTrimRangeCreator(trimRange.id, true);
    case UPDATE_TRIM_OPERATION:
      return updateTrimRangeCreator(trimRange, true);
    default:
      sentry.logException(
        UNKNOWN_TRIM_OPERATION,
        `unkown trim operation ${type}`
      );

      return {};
  }
}

function getComplementTrimOperation(trimOperation, currentTrimRange) {
  const { trimRange, type } = trimOperation;

  switch (type) {
    case ADD_TRIM_OPERATION:
      return { type: REMOVE_TRIM_OPERATION, trimRange };
    case REMOVE_TRIM_OPERATION:
      return { type: ADD_TRIM_OPERATION, trimRange };
    case UPDATE_TRIM_OPERATION:
      return { type: UPDATE_TRIM_OPERATION, trimRange: currentTrimRange };
    default:
      sentry.logException(
        UNKNOWN_TRIM_OPERATION,
        `unkown trim operation ${type}`
      );

      return {};
  }
}

function sortRanges(ranges) {
  return _sortBy(ranges, range => range.from);
}

const stripIds = array => array.map(({ from, to }) => ({ from, to }));

export const enterTrimModeCreator = (trimRanges, postMessage = true) => {
  return (dispatch, getState) => {
    dispatch(updateTheaterMode(false));
    const trimRangesWithoutIds = trimRanges.map(({ from, to }) => ({
      from,
      to,
    }));

    const trimRangesWithIds = trimRanges.map((trimRange, trimRangeIndex) => {
      const newTrimRange = { ...trimRange };

      if (newTrimRange.id == null) {
        // ensure each ID is unique
        newTrimRange.id = Number(new Date()) + trimRangeIndex;
      }

      return newTrimRange;
    });

    // keep the share video control bar and embed player in sync
    if (postMessage) {
      window.top.postMessage(
        {
          type: TRIM_ACTION_DISPATCHED,
          operation: ENTER_TRIM_MODE_OPERATION,
          trimRanges: trimRangesWithIds,
        },
        '*'
      );
    }

    const { duration } = getState().video_player;

    // guarantee we display the whole loom duration even if the video has multiple clips
    dispatch(updateVideoPlayerDisplayedDuration(duration));

    dispatch(updateTrimOriginalRanges(trimRangesWithoutIds));
    dispatch(updateTrimRangesCreator(trimRangesWithIds));
    dispatch(enterTrimMode());

    // we need to force the original video source (not the trimmed source)
    // TO DO: Weave: Remove this dispatch to update the video player source creator
    return dispatch(
      updateVideoPlayerSourceCreator({ forceOriginal: true })
    ).then(() => dispatch(updateTrimRangesCreator(trimRangesWithIds)));
  };
};

export const leaveTrimModeCreator = (
  updatedVideo = null,
  postMessage = true
) => {
  return (dispatch, getState) => {
    // keep the share video control bar and embed player in sync
    if (postMessage) {
      window.top.postMessage(
        {
          type: TRIM_ACTION_DISPATCHED,
          operation: LEAVE_TRIM_MODE_OPERATION,
          updatedVideo,
        },
        '*'
      );
    }

    // first leave trim mode so the creators below can use the `in_trim_mode`
    // trimming state if need be (e.g. updateVideoCreator checking if it should
    // begin polling for the video source or forego it if a user is waiting on a
    // trim to complete)
    dispatch(leaveTrimMode());
    dispatch(updateShareVideoInTrimMode(false));

    const { current_time: currentTime } = getState().video_player;
    const { trim_ranges: trimRanges } = getState().trimming;

    // if we're coming from the patchTrimVideoCreator
    if (updatedVideo != null) {
      // if we patched the video, start from the beginning
      dispatch(updateVideoPlayerCurrentTime(0));
      dispatch(updateVideoCreator(updatedVideo));
    } else {
      // if we're just leaving trim mode, then continue where we left off
      dispatch(
        updateVideoPlayerCurrentTime(
          convertFullTimeToTrimTime(currentTime * 1000, trimRanges) / 1000
        )
      );

      dispatch(updateVideoPlayerSourceCreator());
    }
  };
};

// This is to be used by the new Weave edit mode.
export const leaveEditModeCreator = (
  updatedVideo = null,
  postMessage = true
) => {
  return dispatch => {
    // keep the share video control bar and embed player in sync
    if (postMessage) {
      window.top.postMessage(
        {
          type: TRIM_ACTION_DISPATCHED,
          operation: LEAVE_TRIM_MODE_OPERATION,
          updatedVideo,
        },
        '*'
      );
    }

    // first leave trim mode so the creators below can use the `in_trim_mode`
    // trimming state if need be (e.g. updateVideoCreator checking if it should
    // begin polling for the video source or forego it if a user is waiting on a
    // trim to complete)
    dispatch(leaveTrimMode());
    dispatch(requestVideoPlayerPause());
    dispatch(updateShareVideoInTrimMode(false));
    dispatch(updateVideoPlayerDisplayedDuration(null));
    dispatch(updateVideoPlayerTimeOffset(0));
    dispatch(updateVideoPlayerSourceCreator());
  };
};

export const addTrimRangeCreator = (
  trimRange,
  fromUndoOrRedo = false,
  postMessage = true
) => {
  return (dispatch, getState) => {
    const { trim_ranges: trimRanges, undo_stack: undoStack } =
      getState().trimming;

    const newTrimRange = { ...trimRange };

    // undo/redo operations may reuse old trim Ids, we should keep those
    if (!fromUndoOrRedo && trimRange.id == null) {
      newTrimRange.id = Number(new Date());
    }

    const newRanges = sortRanges([..._cloneDeep(trimRanges), newTrimRange]);

    if (!fromUndoOrRedo) {
      const newUndoStack = [
        ..._cloneDeep(undoStack),
        getComplementTrimOperation({
          type: ADD_TRIM_OPERATION,
          trimRange: newTrimRange,
        }),
      ];

      dispatch(updateTrimUndoStack(newUndoStack));
      dispatch(updateTrimRedoStack([]));

      // keep the share video control bar and embed player in sync
      if (postMessage) {
        window.top.postMessage(
          {
            type: TRIM_ACTION_DISPATCHED,
            operation: ADD_TRIM_OPERATION,
            trimRange: newTrimRange,
          },
          '*'
        );
      }
    }

    dispatch(updateTrimRangesCreator(newRanges));
  };
};

export const removeTrimRangeCreator = (
  trimRangeId,
  fromUndoOrRedo = false,
  postMessage = true
) => {
  return (dispatch, getState) => {
    const {
      current_trim_range: currentTrimRange,
      trim_ranges: trimRanges,
      undo_stack: undoStack,
    } = getState().trimming;

    const trimRange = _find(trimRanges, ({ id }) => id === trimRangeId);

    if (trimRange == null) {
      return;
    }

    const clonedRanges = _cloneDeep(_without(trimRanges, trimRange));
    const newRanges = sortRanges(clonedRanges);

    if (!fromUndoOrRedo) {
      const newUndoStack = [
        ..._cloneDeep(undoStack),
        getComplementTrimOperation({
          type: REMOVE_TRIM_OPERATION,
          trimRange,
        }),
      ];

      dispatch(updateTrimUndoStack(newUndoStack));
      dispatch(updateTrimRedoStack([]));

      // keep the share video control bar and embed player in sync
      if (postMessage) {
        window.top.postMessage(
          {
            type: TRIM_ACTION_DISPATCHED,
            operation: REMOVE_TRIM_OPERATION,
            id: trimRangeId,
          },
          '*'
        );
      }
    }

    dispatch(updateTrimRangesCreator(newRanges));

    if (currentTrimRange != null && currentTrimRange.id !== trimRangeId) {
      dispatch(updateCurrentTrimRangeCreator(currentTrimRange));
    }
  };
};

export const updateTrimRangeCreator = (
  newTrimRange,
  fromUndoOrRedo = false,
  postMessage = true
) => {
  return (dispatch, getState) => {
    const { trim_ranges: trimRanges, undo_stack: undoStack } =
      getState().trimming;

    const currentTrimRange = _find(
      trimRanges,
      ({ id }) => id === newTrimRange.id
    );

    if (currentTrimRange == null) {
      return;
    }

    const newRanges = sortRanges([
      ..._cloneDeep(_without(trimRanges, currentTrimRange)),
      newTrimRange,
    ]);

    if (!fromUndoOrRedo) {
      const newUndoStack = [
        ..._cloneDeep(undoStack),
        getComplementTrimOperation(
          {
            type: UPDATE_TRIM_OPERATION,
          },
          currentTrimRange
        ),
      ];

      dispatch(updateTrimUndoStack(newUndoStack));
      dispatch(updateTrimRedoStack([]));

      // keep the share video control bar and embed player in sync
      if (postMessage) {
        window.top.postMessage(
          {
            type: TRIM_ACTION_DISPATCHED,
            operation: UPDATE_TRIM_OPERATION,
            trimRange: newTrimRange,
          },
          '*'
        );
      }
    }

    dispatch(updateTrimRangesCreator(newRanges));
  };
};

export const undoTrimOperationCreator = (postMessage = true) => {
  return (dispatch, getState) => {
    const {
      redo_stack: redoStack,
      trim_ranges: trimRanges,
      undo_stack: undoStack,
    } = getState().trimming;

    const newUndoStack = _cloneDeep(undoStack);
    const undoOperation = newUndoStack.pop();

    dispatch(callCreatorForTrimOperation(undoOperation));
    dispatch(updateTrimUndoStack(newUndoStack));

    const currentTrimRange = _find(trimRanges, ({ id }) => {
      return id === undoOperation.trimRange.id;
    });

    const redoOperation = getComplementTrimOperation(
      undoOperation,
      currentTrimRange
    );
    const newRedoStack = [..._cloneDeep(redoStack), redoOperation];

    dispatch(updateTrimRedoStack(newRedoStack));

    // keep the share video control bar and embed player in sync
    if (postMessage) {
      window.top.postMessage(
        {
          type: TRIM_ACTION_DISPATCHED,
          operation: UNDO_TRIM_OPERATION,
        },
        '*'
      );
    }
  };
};

export const redoTrimOperationCreator = (postMessage = true) => {
  return (dispatch, getState) => {
    const {
      redo_stack: redoStack,
      trim_ranges: trimRanges,
      undo_stack: undoStack,
    } = getState().trimming;

    const newRedoStack = _cloneDeep(redoStack);
    const redoOperation = newRedoStack.pop();

    dispatch(callCreatorForTrimOperation(redoOperation));
    dispatch(updateTrimRedoStack(newRedoStack));

    const currentTrimRange = _find(trimRanges, ({ id }) => {
      return id === redoOperation.trimRange.id;
    });

    const undoOperation = getComplementTrimOperation(
      redoOperation,
      currentTrimRange
    );
    const newUndoStack = [..._cloneDeep(undoStack), undoOperation];

    dispatch(updateTrimUndoStack(newUndoStack));

    // keep the share video control bar and embed player in sync
    if (postMessage) {
      window.top.postMessage(
        {
          type: TRIM_ACTION_DISPATCHED,
          operation: REDO_TRIM_OPERATION,
        },
        '*'
      );
    }
  };
};

export const patchTrimVideoCreator = (video, onFinish = () => {}) => {
  return (dispatch, getState) => {
    dispatch(setSendingNewTrim(true));

    const { trim_ranges: trimRanges } = getState().trimming;
    const { id: videoId } = video;

    const { video_properties: videoProperties = {} } = video;
    const { duration } = videoProperties;

    const totalTrimTime = getTrimVideoDuration(duration, trimRanges);

    let minimumTotalTimeString = `${MINIMUM_TOTAL_VIDEO_TIME} second`;

    if (MINIMUM_TOTAL_VIDEO_TIME > 1) {
      minimumTotalTimeString += 's';
    }

    if (totalTrimTime < MINIMUM_TOTAL_VIDEO_TIME) {
      return window.alert(
        "Woops! You can't publish a video that's less than " +
          `${minimumTotalTimeString} long.`
      );
    }

    const body = JSON.stringify({
      processing_information: {
        trim_ranges: trimRanges.map(trimRange =>
          _pick(trimRange, 'from', 'to')
        ),
      },
    });

    return fetch(`/v1/videos/${videoId}`, {
      credentials: 'include',
      headers: { [HEADER_CONTENT_TYPE]: JSON_MIME },
      method: 'PATCH',
      body,
    })
      .then(resp => {
        if (resp.status >= 400) {
          throw new Error(
            `Oops! Error trimming video - HTTP response: ${resp.status}`
          );
        }

        // ensure we got back a serializable return type
        return resp.json();
      })
      .then(patchedVideo => {
        // update the processing information for the share video view
        dispatch(updateShareVideo(patchedVideo));
        // disables the Download button until the new trimmed MP4 is available
        trimRanges.length && dispatch(updateShareVideoDownloadUrl(null));

        return dispatch(leaveTrimModeCreator(patchedVideo));
      })
      .then(() => {
        if (trimRanges.length > 0) {
          analytics.track(
            TRIM_PUBLISHED,
            getTrimEventProperties(video, trimRanges, duration)
          );
        }

        onFinish();
      })
      .catch(err => {
        sentry.logException(err, 'error in trim video creator');
        dispatch(showErrorBar(ERROR, err.toString()));
      })
      .finally(() => {
        dispatch(setSendingNewTrim(false));
      });
  };
};

export const updateCurrentTrimRangeCreator = (
  trimRange,
  postMessage = true
) => {
  return dispatch => {
    // keep the share video control bar and embed player in sync
    if (postMessage) {
      window.top.postMessage(
        {
          type: TRIM_ACTION_DISPATCHED,
          operation: UPDATE_CURRENT_TRIM_RANGE_OPERATION,
          trimRange,
        },
        '*'
      );
    }

    return dispatch(updateCurrentTrimRange(trimRange));
  };
};

export const updateSelectedTrimBoundCreator = (
  trimBound,
  postMessage = true
) => {
  return dispatch => {
    // keep the share video control bar and embed player in sync
    if (postMessage) {
      window.top.postMessage(
        {
          type: TRIM_ACTION_DISPATCHED,
          operation: UPDATE_SELECTED_TRIM_BOUND_OPERATION,
          trimBound,
        },
        '*'
      );
    }

    return dispatch(updateSelectedTrimBound(trimBound));
  };
};

export const updateSelectedTrimRangeCreator = (
  trimRange,
  postMessage = true
) => {
  return dispatch => {
    // keep the share video control bar and embed player in sync
    if (postMessage) {
      window.top.postMessage(
        {
          type: TRIM_ACTION_DISPATCHED,
          operation: UPDATE_SELECTED_TRIM_RANGE_OPERATION,
          trimRange,
        },
        '*'
      );
    }

    return dispatch(updateSelectedTrimRange(trimRange));
  };
};

export const updateTrimRangesCreator = newTrimRanges => {
  return (dispatch, getState) => {
    const { original_ranges: originalRanges, trim_ranges: prevTrimRanges } =
      getState().trimming;

    // due to combining redux stores for iframe and window we get duplicate
    // actions and thus multiple trim ranges with the same id, we put in this
    // stopgap till we remove the extraneous message passing and dupe actions
    const actualNewTrimRanges = _uniqBy(newTrimRanges, 'id');
    const newTrimRangesNoIds = stripIds(actualNewTrimRanges);
    const prevTrimRangesNoIds = stripIds(prevTrimRanges);
    const changesFromOriginal = !_isEqual(newTrimRangesNoIds, originalRanges);
    const newChanges = !_isEqual(prevTrimRangesNoIds, newTrimRangesNoIds);

    // For the time being, until the old trimming experience is removed, we
    // are keeping trim ranges and play ranges both in Redux and in sync.
    // We have to do this because we can have multiple play ranges that are
    // contiguous, but if we update only the trim ranges and derive the play
    // ranges from that, the continguous ranges will merge.

    const { duration } = getState().video_player;

    const newPlayRanges = makePlayRangesFromTrimRanges(
      actualNewTrimRanges,
      duration
    );

    dispatch(updateTrimNewChanges(changesFromOriginal));

    if (newChanges) {
      dispatch(updateTrimRanges(actualNewTrimRanges));
      dispatch(resetPlayRangeUndoStack(newPlayRanges));
      dispatch(updatePlayRanges(newPlayRanges));
    }
  };
};

export const updatePlayRangesCreator = newPlayRanges => {
  return (dispatch, getState) => {
    // For the time being, until the old trimming experience is removed, we
    // are keeping trim ranges and play ranges both in Redux and in sync.
    // We have to do this because we can have multiple play ranges that are
    // contiguous, but if we update only the trim ranges and derive the play
    // ranges from that, the contiguous ranges will merge.
    const { original_ranges: originalTrimRanges, playRanges: prevPlayRanges } =
      getState().trimming;
    const { duration } = getState().video_player;

    const newPlayRangesWithoutId = stripIds(newPlayRanges);
    const prevPlayRangesWithoutId = stripIds(prevPlayRanges);

    const newTrimRanges = makeTrimRangesFromPlayRanges(newPlayRanges, duration);
    const newTrimRangesWithoutId = stripIds(newTrimRanges);

    const changesFromOriginal = !_isEqual(
      newTrimRangesWithoutId,
      originalTrimRanges
    );

    const newChanges = !_isEqual(
      newPlayRangesWithoutId,
      prevPlayRangesWithoutId
    );

    dispatch(updateTrimNewChanges(changesFromOriginal));

    if (newChanges) {
      dispatch(addToPlayRangeUndoStack(newPlayRanges));
      dispatch(updatePlayRanges(newPlayRanges));
      dispatch(updateTrimRanges(newTrimRanges));
    }
  };
};

export const syncPlayRangesFromTrimRangesCreator = () => {
  return (dispatch, getState) => {
    // For the time being, until the old trimming experience is removed, we
    // are keeping trim ranges and play ranges both in Redux and in sync.
    // We have to do this because we can have multiple play ranges that are
    // contiguous, but if we update only the trim ranges and derive the play
    // ranges from that, the continguous ranges will merge.
    const { trim_ranges: trimRanges } = getState().trimming;
    const { duration } = getState().video_player;

    const newPlayRanges = makePlayRangesFromTrimRanges(trimRanges, duration);

    dispatch(resetPlayRangeUndoStack(newPlayRanges));
    dispatch(updatePlayRanges(newPlayRanges));
  };
};

export const splitPlayRangeCreator = () => {
  return (dispatch, getState) => {
    // creates two new play ranges from the original selected play
    // range that is now split

    const playRanges = selectPlayRanges(getState());
    let currentTime = selectPlayerTime(getState());
    const playRangeIdx = selectSelectedPlayRangeIdx(getState());

    const { from, to } = playRanges[playRangeIdx];

    if (
      currentTime - from < MIN_PLAY_RANGE_DURATION &&
      to - currentTime < MIN_PLAY_RANGE_DURATION
    ) {
      return;
    } else if (currentTime - from < MIN_PLAY_RANGE_DURATION) {
      currentTime = from + MIN_PLAY_RANGE_DURATION;
    } else if (to - currentTime < MIN_PLAY_RANGE_DURATION) {
      currentTime = to - MIN_PLAY_RANGE_DURATION;
    }

    const newSplitPlayRanges = [
      {
        id: makePlayRangeId(),
        from,
        to: currentTime,
      },
      {
        id: makePlayRangeId(),
        from: currentTime,
        to,
      },
    ];

    const newPlayRanges = playRanges
      .slice(0, playRangeIdx)
      .concat(newSplitPlayRanges, playRanges.slice(playRangeIdx + 1));

    dispatch(updatePlayRangesCreator(newPlayRanges));
  };
};

export const deletePlayRangeCreator = () => {
  return (dispatch, getState) => {
    const playRanges = selectPlayRanges(getState());
    const playRangeIdx = selectSelectedPlayRangeIdx(getState());

    const newPlayRanges = playRanges
      .slice(0, playRangeIdx)
      .concat(playRanges.slice(playRangeIdx + 1));

    if (newPlayRanges[playRangeIdx]) {
      dispatch(requestVideoPlayerCurrentTime(newPlayRanges[playRangeIdx].from));
    } else {
      dispatch(requestVideoPlayerCurrentTime(newPlayRanges[0].from));
      dispatch(requestVideoPlayerPause());
    }

    dispatch(updatePlayRangesCreator(newPlayRanges));
  };
};

const postUndoRedo = () => {
  return (dispatch, getState) => {
    const duration = selectVideoDuration(getState());
    const playRanges = selectPlayRanges(getState());
    const newTrimRanges = makeTrimRangesFromPlayRanges(playRanges, duration);
    const { original_ranges: originalTrimRanges } = getState().trimming;
    const newTrimRangesWithoutId = stripIds(newTrimRanges);

    const changesFromOriginal = !_isEqual(
      newTrimRangesWithoutId,
      originalTrimRanges
    );

    dispatch(updateTrimRanges(newTrimRanges));
    dispatch(updateTrimNewChanges(changesFromOriginal));
  };
};

export const undoPlayRangeChangeCreator = () => {
  return dispatch => {
    dispatch(undoPlayRangeChange());
    dispatch(postUndoRedo());
  };
};

export const redoPlayRangeChangeCreator = () => {
  return dispatch => {
    dispatch(redoPlayRangeChange());
    dispatch(postUndoRedo());
  };
};
